import React, { useContext } from 'react';
import { MenuItem, ListItemIcon, ListItemText, CircularProgress } from '@mui/material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import { useRejectPracticalEvidenceMutation } from '../../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import * as actions from '../../../Reducers/Actions';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';

/**
 * RejectPracticalEvidence
 * Renders a MenuItem that rejects a practical evidence.
 *
 * Props:
 * - evidenceId: string | number (required)
 * - reviewComment?: string
 * - onRejected?: (payload) => void  // receives mutation response data
 * - onError?: (error) => void
 */
export default function RejectPracticalEvidence({ evidenceId, reviewComment, onRejected, onError, closeParentMenu }) {
  const [reject, { isLoading }] = useRejectPracticalEvidenceMutation();
  const dispatch = useDispatch();
  const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);

  const handleReject = async () => {
    if (!evidenceId || isLoading) return;
    try {
      const payload = await reject({
        evidenceId: String(evidenceId),
        body: reviewComment ? { review_comment: reviewComment } : {},
      }).unwrap();
      // Optimistically update redux list
      dispatch({ type: actions.REJECT_PRACTICAL_EVIDENCE, payload: (payload?.data || { evidenceId }) });
      // Snackbar success
      setInfoAlert({ severity: 'success', message: 'Evidence rejected successfully' });
      setOpenSnackBar(true);
      if (onRejected) onRejected(payload);
      if (typeof closeParentMenu === 'function') closeParentMenu();
    } catch (err) {
      const msg = err?.data?.message || err?.message || 'Failed to reject evidence';
      setInfoAlert({ severity: 'error', message: msg });
      setOpenSnackBar(true);
      if (onError) onError(err);
    }
  };

  return (
    <MenuItem onClick={handleReject} disabled={!evidenceId || isLoading}>
      <ListItemIcon>
        {isLoading ? <CircularProgress size={18} /> : <BlockOutlinedIcon fontSize="small" color="error" />}
      </ListItemIcon>
      <ListItemText primary="Reject" />
    </MenuItem>
  );
}

